// MemberList.js - Component to display and filter members
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { 
  TextField, 
  Autocomplete, 
  Grid, 
  Box, 
  Paper, 
  InputAdornment,
  Chip,
  Typography,
  CircularProgress,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import MemberListItem from '../memberListItem/view';
import NoResultsPlaceholder from 'components/organisms/noResultsPlaceholder/view';
import { getGroupMembers, getTagsWithCategory } from 'api';

const MemberList = ({ community, setSelectedParticipant, role }) => {
  const [memberList, setMemberList] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchFilter, setSearchFilter] = useState({
    searchQuery: '',
    tags: [],
    community: community?.slug
  });
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Fetch tags data
  const getTagsInfo = () => {
    setLoading(true);
    getTagsWithCategory()
      .then(response => {
        const selectedTagNames = response.tags?.map(tag => tag.name) || [];
        setTags(selectedTagNames);
      })
      .catch(error => {
        console.error('Error fetching tags:', error);
        setError('Failed to load tags');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch members based on search filters
  const getMemberList = () => {
    setLoading(true);
    getGroupMembers(searchFilter)
      .then(response => {
        setMemberList(response.members || []);
        setError(null);
      })
      .catch(error => {
        console.error('Error fetching members:', error);
        setError('Failed to load members');
        setMemberList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Update member list when search filters change
  useEffect(() => {
    getMemberList();
  }, [searchFilter]);

  // Update community filter when community changes
  useEffect(() => {
    if (community) {
      setSearchFilter(prev => ({
        ...prev,
        community: community.slug
      }));
    }
  }, [community]);

  // Fetch tags on component mount
  useEffect(() => {
    getTagsInfo();
  }, []);

  // Debounce search input to reduce API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchFilter(prev => ({
        ...prev,
        searchQuery: debouncedSearchQuery,
      }));
    }, 500); // 500ms delay for better user experience

    return () => clearTimeout(handler);
  }, [debouncedSearchQuery]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setDebouncedSearchQuery(e.target.value);
  };

  // Handle tag selection change
  const handleTagChange = (event, value) => {
    setSelectedTags(value);
    setSearchFilter(prev => ({
      ...prev,
      tags: value
    }));
  };

  // Group members by role for better organization
  const groupedMembers = memberList.reduce((acc, member) => {
    const roleKey = member.role || 'other';
    if (!acc[roleKey]) {
      acc[roleKey] = [];
    }
    acc[roleKey].push(member);
    return acc;
  }, {});

  // Get label for role category
  const getRoleLabel = (role) => {
    switch (role) {
      case 'AD':
        return 'Administrators';
      case 'FO':
        return 'Founders';
      case 'MO':
        return 'Moderators';
      case 'PA':
        return 'Participants';
      case 'GU':
        return 'Guests';
      default:
        return 'Other Members';
    }
  };

  // Order of roles for display
  const roleOrder = ['FO', 'AD', 'MO', 'PA', 'GU', 'other'];

  return (
    <Box>
      {/* Search and Filter Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 3, 
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: 2
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Search by Name, Surname, or Alias"
              variant="outlined"
              value={debouncedSearchQuery}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              id="tags"
              options={tags}
              getOptionLabel={(option) => option}
              value={selectedTags}
              onChange={handleTagChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Filter by Tags"
                  placeholder="Select tags"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <FilterListIcon color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.08)', 
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(0, 0, 0, 0.54)',
                      }
                    }}
                  />
                ))
              }
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && memberList.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Paper sx={{ p: 3, bgcolor: '#fff8f8', color: '#d32f2f', mb: 3, borderRadius: 2 }}>
          <Typography>{error}</Typography>
        </Paper>
      )}

      {/* No Results */}
      {!loading && memberList.length === 0 && (
        <NoResultsPlaceholder 
          firstSentence="No members found" 
          lastSentence="Try adjusting your search criteria or filters" 
        />
      )}

      {/* Member Grid by Role Category */}
      {!loading && memberList.length > 0 && (
        <Box>
          {roleOrder.map(roleKey => {
            const members = groupedMembers[roleKey] || [];
            if (members.length === 0) return null;
            
            return (
              <Box key={roleKey} sx={{ mb: 4 }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" component="h2">
                    {getRoleLabel(roleKey)}
                  </Typography>
                  <Chip 
                    label={members.length} 
                    size="small" 
                    sx={{ ml: 1, bgcolor: 'rgba(0, 0, 0, 0.08)' }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {members.map((member, index) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={1.5} key={index}>
                      <MemberListItem 
                        member={member} 
                        setSelectedParticipant={setSelectedParticipant} 
                        role={role}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

MemberList.propTypes = {
  community: PropTypes.object,
  members: PropTypes.array,
  setSelectedParticipant: PropTypes.func,
  role: PropTypes.string,
};

export default MemberList;